import { zodResolver } from '@hookform/resolvers/zod';
import charityVettingApiSlice from 'api/charity-vetting/charityVettingApiSlice';
import { getStepNameFromPath } from 'features/charity-vetting/common-flow/entries/path';
import {
  getPageSchema,
  type PageSchema,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';
import { useVettingDocuments } from 'features/charity-vetting/country-specific-flows/fr-flow/useVettingDocuments';
import {
  CountryCodeEnum,
  type CharityVettingFormValues,
} from 'features/charity-vetting/types/charityVettingTypes';
import { parseCountryParam } from 'features/charity-vetting/utils/countryHandlers';
import { handleInitialValues } from 'features/charity-vetting/utils/initialFormHandlers';
import {
  getCharityVettingFormValues,
  getCountryCodeFromStorage,
} from 'features/charity-vetting/utils/storageHandlers';
import {
  fromCharityDocuments,
  fromVettingRequestData,
} from 'features/charity-vetting/utils/vettingApiPayload';
import { type PropsWithChildren } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

/**
 * Owns a single React Hook Form instance for the current vetting page.
 *
 * It is mounted once and re-keyed by route pathname (see CharityVettingEntry),
 * so navigating between steps remounts a fresh form scoped to that page — the
 * "one form per page" model. The page's Zod schema is resolved from the step
 * name; `defaultValues` are hydrated from the (transient) localStorage carrier.
 *
 * The whole charityOnboarding object is provided as defaultValues so the active
 * page's fields are pre-filled and the shared progress/country state is carried
 * across pages until the future charity API replaces this carrier.
 */
const definedValues = <T extends object>(values: T | null | undefined): Partial<T> => {
  if (!values) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
};

const toFormResolver = (schema: PageSchema): Resolver<CharityVettingFormValues> =>
  zodResolver(schema) as unknown as Resolver<CharityVettingFormValues>;

const CharityVettingFormProvider = ({ children }: PropsWithChildren) => {
  const { formatMessage } = useIntl();
  const { pathname, search } = useLocation();

  const { data: vetting } = charityVettingApiSlice.endpoints.getCharityVetting.useQuery();

  const countryIsoCode =
    parseCountryParam(search) ??
    getCountryCodeFromStorage() ??
    vetting?.countryIsoCode ??
    CountryCodeEnum.GB;

  const { documents } = useVettingDocuments(countryIsoCode === CountryCodeEnum.FR);

  const stepName = getStepNameFromPath(pathname);
  const schema = getPageSchema(stepName, formatMessage, countryIsoCode);
  const serverValues = vetting
    ? { ...fromVettingRequestData(vetting), vettingDocuments: fromCharityDocuments(documents) }
    : undefined;

  const baseValues = handleInitialValues(countryIsoCode);
  const initialValues = {
    ...baseValues,
    charityOnboarding: {
      ...baseValues.charityOnboarding,
      ...definedValues(serverValues),
      ...definedValues(getCharityVettingFormValues()),
      countryIsoCode,
    },
  };

  const methods = useForm<CharityVettingFormValues>({
    resolver: toFormResolver(schema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export default CharityVettingFormProvider;
